const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Configuración Global de Campañas y Calentamiento",
    summary:
      "Ajusta los parámetros operativos y límites de seguridad para el despacho de campañas masivas por WhatsApp. Configura intervalos aleatorios orgánicos, descansos por fatiga simulada y conoce la metodología de maduración de líneas por Tiers para evitar bloqueos de Meta.",
  },
  sections: [
    {
      id: "parametros_seguridad",
      title: "1. Intervalos de Seguridad y Anti-Bloqueo",
      description:
        "Definición de pausas automáticas entre mensajes consecutivos para simular actividad humana real.",
      items: [
        {
          id: "intervalo_disparo",
          title: "Intervalo de Disparo Aleatorio (Tiempo Base)",
          definition:
            "Define el tiempo base promedio entre mensajes (ej. 300 segundos / 5 minutos). BotMix aplica un algoritmo de dispersión asimétrica (human jitter) que oscila entre lapsos más breves y más prolongados para que ningún envío tenga la misma cadencia.",
        },
        {
          id: "intervalo_mayor_despues",
          title: "Intervalo Mayor Después de (Bloque de Mensajes)",
          definition:
            "Determina cuántos mensajes consecutivos se despacharán antes de que el sistema detenga el envío para tomar un descanso prolongado (recomendado: 10 mensajes).",
        },
        {
          id: "intervalo_mayor",
          title: "Intervalo de Envío Mayor (Pausa de Descanso)",
          definition:
            "Tiempo de enfriamiento durante la pausa prolongada (ej. 600 segundos / 10 minutos). Cada vez que la campaña completa un nuevo bloque de mensajes, el sistema suma 1 minuto adicional de forma automática para emular el cansancio de un operador humano.",
        },
      ],
    },
    {
      id: "calentamiento_tiers",
      title: "2. Sistema de Calentamiento Progresivo (Tiers)",
      description:
        "Metodología escalonada para proteger líneas nuevas y maximizar la entregabilidad.",
      items: [
        {
          id: "tier_1",
          title: "Tier 1: Línea Nueva o Primera Campaña (Hasta 30 msgs/día)",
          definition:
            "Para chips nuevos o recién conectados a BotMix. Permite verificar la temperatura del número y evitar disparar alertas de spam tempranas en Meta.",
        },
        {
          id: "tier_2",
          title: "Tier 2: En Progreso (Hasta 75 msgs/día)",
          definition:
            "Se habilita tras completar las primeras campañas exitosas sin desconexiones abruptas ni bloqueos reportados por destinatarios.",
        },
        {
          id: "tier_3",
          title: "Tier 3: Estable (Hasta 150 msgs/día)",
          definition:
            "Línea con historial confiable y conversaciones bidireccionales constantes. Admite volúmenes intermedios seguros.",
        },
        {
          id: "tier_4",
          title: "Tier 4: Conexión Madura (Hasta 250 msgs/día)",
          definition:
            "Volumen máximo diario recomendado para transmisiones masivas sin riesgo de degradación de calidad.",
        },
      ],
    },
  ],
};

const en = {
  header: {
    badge: "BotMix Official Guide",
    title: "Global Campaign Settings & Warmup System",
    summary:
      "Configure operational rules and safety limits for bulk WhatsApp broadcasts. Set up organic non-linear delays, simulated fatigue rest breaks, and learn how line reputation tiers protect your numbers from Meta bans.",
  },
  sections: [
    {
      id: "parametros_seguridad",
      title: "1. Safety Intervals & Anti-Ban Controls",
      description:
        "Define automatic cooldown periods between consecutive message dispatches to emulate human pacing.",
      items: [
        {
          id: "intervalo_disparo",
          title: "Random Dispatch Interval (Base Timing)",
          definition:
            "Sets the baseline delay between messages (e.g., 300 seconds / 5 minutes). BotMix applies asymmetric human jitter oscillating between short and long gaps so no two dispatches have the same timing.",
        },
        {
          id: "intervalo_mayor_despues",
          title: "Longer Interval After (Message Batch)",
          definition:
            "Specifies how many consecutive messages are sent before taking an extended rest break (recommended: 10 messages).",
        },
        {
          id: "intervalo_mayor",
          title: "Greater Dispatch Interval (Cool-Down Break)",
          definition:
            "Length of the extended rest pause (e.g., 600 seconds / 10 minutes). With every batch completed, BotMix automatically adds 1 extra minute to simulate natural human fatigue.",
        },
      ],
    },
    {
      id: "calentamiento_tiers",
      title: "2. Progressive Warmup System (Tiers)",
      description:
        "Gradual scaling methodology to protect new phone numbers and maximize inbox delivery.",
      items: [
        {
          id: "tier_1",
          title: "Tier 1: Fresh Number / First Campaign (Up to 30 msgs/day)",
          definition:
            "For new SIM cards or newly connected channels. Validates line health without triggering Meta spam algorithms.",
        },
        {
          id: "tier_2",
          title: "Tier 2: In Progress (Up to 75 msgs/day)",
          definition:
            "Achieved after running initial broadcasts with zero disconnects and no customer spam flags.",
        },
        {
          id: "tier_3",
          title: "Tier 3: Stable (Up to 150 msgs/day)",
          definition:
            "For seasoned channels with steady two-way customer dialogues. Allows safe medium-tier broadcasts.",
        },
        {
          id: "tier_4",
          title: "Tier 4: Mature Connection (Up to 250 msgs/day)",
          definition:
            "Maximum recommended daily outreach quota for mature numbers to preserve green quality ratings.",
        },
      ],
    },
  ],
};

const pt = {
  header: {
    badge: "Guia Oficial do BotMix",
    title: "Configurações Globais de Campanhas e Aquecimento",
    summary:
      "Ajuste parâmetros operacionais e limites de segurança para disparos em massa no WhatsApp. Configure intervalos aleatórios, descansos por fadiga simulada e conheça o sistema de tiers de aquecimento anti-ban.",
  },
  sections: [
    {
      id: "parametros_seguridad",
      title: "1. Intervalos de Segurança Anti-Bloqueio",
      description:
        "Definição de pausas automáticas entre mensagens para emular o comportamento humano real.",
      items: [
        {
          id: "intervalo_disparo",
          title: "Intervalo de Disparo Aleatório (Tempo Base)",
          definition:
            "Define o tempo base médio entre mensagens (ex: 300 segundos / 5 minutos). O BotMix aplica jitter orgânico oscilando entre tempos curtos e longos para evitar cadências robóticas.",
        },
        {
          id: "intervalo_mayor_despues",
          title: "Intervalo Maior Após (Bloco de Mensagens)",
          definition:
            "Determina quantas mensagens consecutivas serão disparadas antes de aplicar uma pausa longa (recomendado: 10 mensagens).",
        },
        {
          id: "intervalo_mayor",
          title: "Intervalo de Envio Maior (Pausa de Descanso)",
          definition:
            "Tempo de descanso prolongado (ex: 600 segundos / 10 minutos). A cada novo bloco concluído, o sistema soma 1 minuto extra automaticamente para simular o cansaço humano.",
        },
      ],
    },
    {
      id: "calentamiento_tiers",
      title: "2. Sistema de Aquecimento Progressivo (Tiers)",
      description:
        "Metodologia escalonada para proteger números novos e garantir alta entregabilidade.",
      items: [
        {
          id: "tier_1",
          title: "Tier 1: Linha Nova ou Primeira Campanha (Até 30 msgs/dia)",
          definition:
            "Para números recém-conectados ao BotMix. Valida a reputação sem ativar os filtros anti-spam da Meta.",
        },
        {
          id: "tier_2",
          title: "Tier 2: Em Progresso (Até 75 msgs/dia)",
          definition:
            "Habilitado após campanhas bem-sucedidas sem quedas de conexão ou denúncias de spam.",
        },
        {
          id: "tier_3",
          title: "Tier 3: Estável (Até 150 msgs/dia)",
          definition:
            "Linha com histórico maduro e interações bidirecionais frequentes. Permite volumes médios seguros.",
        },
        {
          id: "tier_4",
          title: "Tier 4: Conexão Madura (Até 250 msgs/dia)",
          definition:
            "Volume máximo diário seguro para transmissões em massa sem degradação da qualidade da linha.",
        },
      ],
    },
  ],
};

const fr = {
  header: {
    badge: "Guide Officiel BotMix",
    title: "Configuration Globale des Campagnes et Préchauffage",
    summary:
      "Ajustez les règles de sécurité et les délais pour les diffusions massives sur WhatsApp. Configurez des intervalles aléatoires organiques, des temps de repos pour fatigue simulée et comprenez les paliers d'échauffement.",
  },
  sections: [
    {
      id: "parametros_seguridad",
      title: "1. Délais de Sécurité et Anti-Blocage",
      description:
        "Définition des pauses automatiques entre chaque message pour émuler un comportement humain réel.",
      items: [
        {
          id: "intervalo_disparo",
          title: "Intervalle d'Envoi Aléatoire (Délai de Base)",
          definition:
            "Définit le temps moyen entre deux messages (ex : 300 secondes / 5 minutes). BotMix applique une variation asymétrique pour éviter toute régularité suspecte.",
        },
        {
          id: "intervalo_mayor_despues",
          title: "Intervalle Majeur Après (Bloc de Messages)",
          definition:
            "Indique combien de messages consécutifs sont envoyés avant de déclencher une pause de repos prolongée (recommandé : 10 messages).",
        },
        {
          id: "intervalo_mayor",
          title: "Intervalle d'Envoi Majeur (Pause de Repos)",
          definition:
            "Durée de la pause prolongée (ex : 600 secondes / 10 minutes). À chaque bloc terminé, le système ajoute automatiquement 1 minute supplémentaire pour simuler la fatigue naturelle.",
        },
      ],
    },
    {
      id: "calentamiento_tiers",
      title: "2. Système de Préchauffage Progressif (Tiers)",
      description:
        "Méthode progressive pour protéger les nouveaux numéros et maximiser la délivrabilité.",
      items: [
        {
          id: "tier_1",
          title: "Tier 1 : Nouveau Numéro / Première Campagne (Jusqu'à 30 msgs/jour)",
          definition:
            "Pour les puces récentes ou nouvellement connectées. Valide la santé du numéro sans alerter les filtres Meta.",
        },
        {
          id: "tier_2",
          title: "Tier 2 : En Cours (Jusqu'à 75 msgs/jour)",
          definition:
            "Débloqué après des envois initiaux réussis sans déconnexions ni signalements d'utilisateurs.",
        },
        {
          id: "tier_3",
          title: "Tier 3 : Stable (Jusqu'à 150 msgs/jour)",
          definition:
            "Ligne expérimentée avec des échanges bidirectionnels réguliers. Permet des volumes intermédiaires sécurisés.",
        },
        {
          id: "tier_4",
          title: "Tier 4 : Connexion Mature (Jusqu'à 250 msgs/jour)",
          definition:
            "Volume quotidien maximal recommandé pour les diffusions sans risque pour la ligne.",
        },
      ],
    },
  ],
};

const ar = {
  header: {
    badge: "الدليل الرسمي لـ BotMix",
    title: "الإعدادات العامة للحملات ونظام الإحماء",
    summary:
      "تهيئة القواعد التشغيلية وفترات الأمان لإرسال رسائل واتساب الجماعية. ضبط الفترات العشوائية العضوية، وفترات الراحة لمحاكاة إجهاد الموظف، والتعرف على مستويات تدفئة الخطوط لتجنب الحظر.",
  },
  sections: [
    {
      id: "parametros_seguridad",
      title: "1. فترات الأمان والحماية من الحظر",
      description:
        "تحديد أوقات الانتظار التلقائية بين الرسائل لمحاكاة السلوك البشري الحقيقي بدقة.",
      items: [
        {
          id: "intervalo_disparo",
          title: "فاصل الإرسال العشوائي (الوقت الأساسي)",
          definition:
            "يحدد متوسط وقت الانتظار الأساسي بين الرسائل (مثل: 300 ثانية / 5 دقائق). يطبق BotMix خوارزمية تشتت غير خطية تمنع النمط المتكرر.",
        },
        {
          id: "intervalo_mayor_despues",
          title: "الفاصل الأكبر بعد (مجموعة الرسائل)",
          definition:
            "يحدد عدد الرسائل المتتالية التي يتم إرسالها قبل أن يتوقف النظام لأخذ استراحة أطول (الموصى به: 10 رسائل).",
        },
        {
          id: "intervalo_mayor",
          title: "فاصل الإرسال الأكبر (استراحة التهدئة)",
          definition:
            "مدة الاستراحة المطولة (مثل: 600 ثانية / 10 دقائق). مع كل مجموعة مكتملة، يضيف النظام دقيقة إضافية تلقائياً لمحاكاة الإجهاد البشري الطبيعي.",
        },
      ],
    },
    {
      id: "calentamiento_tiers",
      title: "2. نظام الإحماء التدريجي (المستويات)",
      description:
        "منهجية تصاعدية لحماية الأرقام الجديدة وتحقيق أعلى معدل وصول للمستلمين.",
      items: [
        {
          id: "tier_1",
          title: "المستوى 1: خط جديد أو أول حملة (حتى 30 رسالة/يوم)",
          definition:
            "للأرقام الجديدة أو المتصلة حديثاً بالنظام للتأكد من سمعة الرقم دون إثارة فلاتر ميتا.",
        },
        {
          id: "tier_2",
          title: "المستوى 2: قيد التقدم (حتى 75 رسالة/يوم)",
          definition:
            "يتم الوصول إليه بعد حملات أولية ناجحة خالية من الانقطاعات أو بلاغات الحظر.",
        },
        {
          id: "tier_3",
          title: "المستوى 3: مستقر (حتى 150 رسالة/يوم)",
          definition:
            "للأرقام ذات الحوارات المتبادلة المستمرة مع العملاء وتتيح بث أحجام متوسطة بأمان.",
        },
        {
          id: "tier_4",
          title: "المستوى 4: اتصال ناضج (حتى 250 رسالة/يوم)",
          definition:
            "الحد الأقصى اليومي الموصى به للبث الجماعي للحفاظ على تصنيف الجودة الأخضر للأرقام.",
        },
      ],
    },
  ],
};

export const CAMPAIGNS_CONFIG_HELP_DATA = { es, en, pt, fr, ar };
